import { fabric } from 'fabric';
import {
  lineLength,
  getPointOnQuadraticBezierIterator,
  getPointOnCubicBezierIterator,
  pointNearQuadraticBezier,
  pointNearCubicBezier,
} from '../../utils/geometry';
import paper from 'paper';
import { ConnectorEnd } from '../../types';
import { type IPoint } from 'fabric/fabric-impl';
import type Connector from '../../objects/Connector';

// Router for creating multi-point bezier curves.
//
// `points` holds the endpoints of each segment of the line.
// `controlPoints` holds the control points for the curve for each segment of the line.
class RouterBezier {
  static computeControlPoints(connector: Connector) {
    if (connector.points.length === 2) {
      this._controlPointsTwoPointBezier(connector);
    } else {
      this._controlPointsMultiPointBezier(connector);
    }
  }

  static _controlPointsTwoPointBezier(connector: Connector) {
    const point0 = connector.points[0];
    const point1 = connector.points[1];
    const length = lineLength(point0, point1) / 2;

    // Compute the default direction for an unattached connector.
    let deltaX = 0;
    let deltaY = 0;
    let deltaDirection = 1;

    // The 1.1 is to create some hysteresis so we don't flick back and forth
    // on a 45 degree shift-drag.
    if (connector.width >= connector.height * 1.1) {
      deltaX = connector.width * 0.6;
      if (point1.x < point0.x) deltaDirection = -1;
    } else {
      deltaY = connector.height * 0.6;
      if (point1.y < point0.y) deltaDirection = -1;
    }

    const controlPoints: IPoint[] = [];
    connector.controlPoints = controlPoints;
    if (connector.attachments[ConnectorEnd.CONNECTOR_START]) {
      this._addControlPointOnNormal(
        connector,
        point0,
        ConnectorEnd.CONNECTOR_START,
        length
      );
    } else {
      controlPoints.push({
        x: point0.x + deltaDirection * deltaX,
        y: point0.y + deltaDirection * deltaY,
      });
    }
    if (connector.attachments[ConnectorEnd.CONNECTOR_END]) {
      this._addControlPointOnNormal(
        connector,
        point1,
        ConnectorEnd.CONNECTOR_END,
        length
      );
    } else {
      controlPoints.push({
        x: point1.x - deltaDirection * deltaX,
        y: point1.y - deltaDirection * deltaY,
      });
    }
  }

  static _addControlPointOnNormal(
    connector: Connector,
    point: IPoint,
    whichEnd: ConnectorEnd,
    length: number
  ) {
    const attachment = connector.attachments[whichEnd]!;

    const object = attachment.object;
    const normal = attachment.normal;
    const rotatedNormal = fabric.util.rotatePoint(
      normal as fabric.Point,
      { x: 0, y: 0 } as fabric.Point,
      fabric.util.degreesToRadians(object.angle || 0)
    );

    connector.controlPoints.push({
      x: point.x + rotatedNormal.x * length,
      y: point.y + rotatedNormal.y * length,
    });
  }

  static _controlPointsMultiPointBezier(connector: Connector) {
    const len = connector.points.length;

    let beforePoint: IPoint | null = null;
    let afterPoint: IPoint | null = null;
    const controlPoints: IPoint[] = [];
    connector.controlPoints = controlPoints;
    for (let i = 0; i < len; i++) {
      const point = connector.points[i];
      afterPoint = connector.points[i + 1];

      if (!beforePoint) {
        if (connector.attachments[ConnectorEnd.CONNECTOR_START]) {
          const length = Math.max(lineLength(point, afterPoint) / 2, 30);
          this._addControlPointOnNormal(
            connector,
            point,
            ConnectorEnd.CONNECTOR_START,
            length
          );
        }
      } else if (!afterPoint) {
        if (connector.attachments[ConnectorEnd.CONNECTOR_END]) {
          const length = Math.max(lineLength(point, beforePoint) / 2, 30);
          this._addControlPointOnNormal(
            connector,
            point,
            ConnectorEnd.CONNECTOR_END,
            length
          );
        }
      } else {
        // Compute the control points as the midpoints of two intersecting
        // lines forming an A shape.
        // Based on figure 7 here: https://www.math.ucla.edu/~baker/149.1.02w/handouts/dd_splines.pdf
        controlPoints.push({
          x: point.x - afterPoint.x / 4 + beforePoint.x / 4,
          y: point.y - afterPoint.y / 4 + beforePoint.y / 4,
        });
        controlPoints.push({
          x: point.x + afterPoint.x / 4 - beforePoint.x / 4,
          y: point.y + afterPoint.y / 4 - beforePoint.y / 4,
        });
      }
      beforePoint = point;
    }
  }

  // Determine if we need to offset controlPoint lookups to avoid the start point interfering with
  // calculations. If there's a connected shape, or if there are no custom control points, there will be a
  // control point at the start that we want to ignore
  private static getControlPointOffset(connector: Connector) {
    return !connector.attachments[ConnectorEnd.CONNECTOR_START] &&
      connector.points.length > 2
      ? -1
      : 0;
  }

  static pointNearLineSegment(
    connector: Connector,
    point: IPoint,
    index: number,
    radius: number
  ) {
    const controlPointOffset = this.getControlPointOffset(connector);

    const cp1 = connector.controlPoints[index * 2 + controlPointOffset];
    const cp2 = connector.controlPoints[1 + index * 2 + controlPointOffset];

    if (!cp1 || !cp2) {
      // if a control point is null, use quadratic.
      const cp = cp1 || cp2;
      return pointNearQuadraticBezier(
        point,
        radius,
        connector.points[index].x,
        connector.points[index].y,
        cp.x,
        cp.y,
        connector.points[index + 1].x,
        connector.points[index + 1].y
      );
    } else {
      return pointNearCubicBezier(
        point,
        radius,
        connector.points[index].x,
        connector.points[index].y,
        cp1.x,
        cp1.y,
        cp2.x,
        cp2.y,
        connector.points[index + 1].x,
        connector.points[index + 1].y
      );
    }
  }

  static midPoint(connector: Connector, afterIndex: number) {
    const controlPointOffset = this.getControlPointOffset(connector);

    // Compute the bezier curve for the segment.
    const cp1 = connector.controlPoints[afterIndex * 2 + controlPointOffset];
    const cp2 =
      connector.controlPoints[1 + afterIndex * 2 + controlPointOffset];

    if (!cp1 || !cp2) {
      // if a control point is null, use quadratic.
      const cp = cp1 || cp2;
      return getPointOnQuadraticBezierIterator(
        connector.points[afterIndex].x,
        connector.points[afterIndex].y,
        cp.x,
        cp.y,
        connector.points[afterIndex + 1].x,
        connector.points[afterIndex + 1].y
      )(0.5);
    } else {
      return getPointOnCubicBezierIterator(
        connector.points[afterIndex].x,
        connector.points[afterIndex].y,
        cp1.x,
        cp1.y,
        cp2.x,
        cp2.y,
        connector.points[afterIndex + 1].x,
        connector.points[afterIndex + 1].y
      )(0.5);
    }
  }

  static pointOnLine(connector: Connector, t: number) {
    const path = this._constructPaperPath(connector);
    const offset = t * path.length;
    const point = path.getPointAt(offset);
    const tangent = path.getTangentAt(offset);

    return { x: point.x, y: point.y, angle: tangent.angle };
  }

  static closestPointOnPath(connector: Connector, point: IPoint) {
    const path = this._constructPaperPath(connector);
    const pointOnPath = path.getNearestPoint(point);

    return path.getOffsetOf(pointOnPath) / path.length;
  }

  // TODO can we cache this?
  static _constructPaperPath(connector: Connector) {
    const path = new paper.Path();
    const point0 = connector.points[0];

    path.moveTo(new paper.Point(point0.x, point0.y));
    if (connector.points.length === 2) {
      const controlPoints = connector.controlPoints;
      const point1 = connector.points[1];
      path.cubicCurveTo(
        new paper.Point(controlPoints[0].x, controlPoints[0].y),
        new paper.Point(controlPoints[1].x, controlPoints[1].y),
        new paper.Point(point1.x, point1.y)
      );
    } else {
      const controlPoints = connector.controlPoints;

      const controlPointOffset = this.getControlPointOffset(connector);

      for (let i = 1; i < connector.points.length; i++) {
        const point = connector.points[i];
        const cp1 = controlPoints[(i - 1) * 2 + controlPointOffset];
        const cp2 = controlPoints[1 + (i - 1) * 2 + controlPointOffset];

        if (!cp1 || !cp2) {
          const cp = cp1 || cp2;
          path.quadraticCurveTo(
            new paper.Point(cp.x, cp.y),
            new paper.Point(point.x, point.y)
          );
        } else {
          path.cubicCurveTo(
            new paper.Point(cp1.x, cp1.y),
            new paper.Point(cp2.x, cp2.y),
            new paper.Point(point.x, point.y)
          );
        }
      }
    }

    return path;
  }

  static drawLine(connector: Connector, ctx: CanvasRenderingContext2D) {
    if (connector.points.length === 2) {
      this._drawTwoPointBezier(connector, ctx);
    } else {
      this._drawMultiPointBezier(connector, ctx);
    }
  }

  static _drawTwoPointBezier(
    connector: Connector,
    ctx: CanvasRenderingContext2D
  ) {
    const x = connector.pathOffset.x;
    const y = connector.pathOffset.y;
    const point0 = connector.points[0];
    const point1 = connector.points[1];
    const controlPoints = connector.controlPoints;

    ctx.moveTo(point0.x - x, point0.y - y);
    ctx.bezierCurveTo(
      controlPoints[0].x - x,
      controlPoints[0].y - y,
      controlPoints[1].x - x,
      controlPoints[1].y - y,
      point1.x - x,
      point1.y - y
    );

    /* Uncomment to debug control points.
    ctx.strokeStyle = 'red';
    ctx.font = '4px serif';
    ctx.strokeRect(controlPoints[0].x - x, controlPoints[0].y - y, 3, 3);
    ctx.strokeRect(controlPoints[1].x - x, controlPoints[1].y - y, 3, 3);
    */
  }

  static _drawMultiPointBezier(
    connector: Connector,
    ctx: CanvasRenderingContext2D
  ) {
    const len = connector.points.length;
    const x = connector.pathOffset.x;
    const y = connector.pathOffset.y;
    const controlPoints = connector.controlPoints;
    const controlPointOffset = this.getControlPointOffset(connector);

    ctx.moveTo(connector.points[0].x - x, connector.points[0].y - y);
    for (let i = 1; i < len; i++) {
      const point = connector.points[i];
      const cp1 = controlPoints[(i - 1) * 2 + controlPointOffset];
      const cp2 = controlPoints[1 + (i - 1) * 2 + controlPointOffset];

      if (!cp1 || !cp2) {
        const cp = cp1 || cp2; // we assume they're not both null
        ctx.quadraticCurveTo(cp.x - x, cp.y - y, point.x - x, point.y - y);

        /* Uncomment to debug control points.
        ctx.strokeStyle = 'red';
        ctx.font = '4px serif';
        ctx.strokeRect(cp.x - x, cp.y - y, 3, 3);
        */
      } else {
        ctx.bezierCurveTo(
          cp1.x - x,
          cp1.y - y,
          cp2.x - x,
          cp2.y - y,
          point.x - x,
          point.y - y
        );

        /* Uncomment to debug control points.
        ctx.strokeStyle = 'red';
        ctx.font = '4px serif';
        ctx.strokeRect(cp1.x - x, cp1.y - y, 3, 3);
        ctx.strokeRect(cp2.x - x, cp2.y - y, 3, 3);
        */
      }
    }
  }

  static arrowHeadLine(
    connector: Connector,
    whichEnd: ConnectorEnd
  ): { from: IPoint; to: IPoint } {
    if (whichEnd === ConnectorEnd.CONNECTOR_START) {
      return {
        from: connector.controlPoints[0],
        to: connector.points[0],
      };
    } else {
      return {
        from: connector.controlPoints[connector.controlPoints.length - 1],
        to: connector.points[connector.points.length - 1],
      };
    }
  }
}

export default RouterBezier;
